import { TypographyT5, TypographyT7 } from '@picsart/design-system/foundation/typography';
import { NeutralColors } from '@picsart/design-system/foundation/colors';

import { memo, useMemo } from 'react';

import { usePulseSegments } from '@pulse/react';

import {
  Button,
  ButtonSkinTertiary,
  ButtonVariants,
} from '@picsart/design-system/components/Button';
import { Text } from '@picsart/design-system/components/Text';
import { FontWeights } from '@picsart/design-system/foundation/fonts';

import {
  ANALYTICS_NAMES,
  loadMoreButtonText,
  moreLikeThisText,
} from 'templates/components/SingleTemplate/constants';

import { useTranslator } from '@picsart/rc/services/localization';
import JSONLinkedData from 'components/JSONLinkedData';
import { SchemaType } from 'components/JSONLinkedData/types';
import { schemaDataMap } from 'components/JSONLinkedData/useCreateSchema';

import TemplateMasonryGrid from 'components/TemplatesMasonryGrid';
import useSingleTemplatesDataStore from 'templates/stores/singleTemplateDataStore';
import FirstElement from 'components/TemplatesMasonryGrid/FirstElement';
import useStyles from './style';
import testIds from './testIds';

const Content = () => {
  const classes = useStyles();
  const translate = useTranslator();

  const { segments } = usePulseSegments();

  const { fetchMore, isLoading, masonryData, isLastPage, singleTemplateData } =
    useSingleTemplatesDataStore(state => ({
      fetchMore: state.fetchMore,
      isLoading: state.isFetchMoreLoading,
      masonryData: state.otherTemplates.data,
      isLastPage: state.isLastPage,
      singleTemplateData: state.singleTemplateData,
    }));

  const schemaData = useMemo(() => schemaDataMap(masonryData), [masonryData]);
  const MemoizedFirstElement = useMemo(
    () => <FirstElement categoryName={singleTemplateData.subtypes[0]} />,
    [singleTemplateData.subtypes],
  );

  return (
    <section className={classes.section}>
      {Boolean(masonryData.length) && (
        <div
          className={classes.root}
          data-testid={testIds.root}
          data-pulse-section={ANALYTICS_NAMES.CONTENT}
        >
          <div className={classes.wrapper}>
            {schemaData && <JSONLinkedData data={schemaData} type={SchemaType.WebPage} />}
            <Text
              data-testid={testIds.moreTemplatesTitle}
              size={TypographyT7}
              weight={FontWeights.bold}
              className={classes.moreTemplates}
            >
              {translate(moreLikeThisText)}
            </Text>
            <TemplateMasonryGrid masonryData={masonryData} FirstElement={MemoizedFirstElement} />
            <div className={classes.loadMoreWrapper}>
              {!isLastPage && (
                <Button
                  isLoading={isLoading}
                  variant={ButtonVariants.Filled}
                  skin={ButtonSkinTertiary}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={() => fetchMore(segments)}
                  dataTestId={testIds.seeMoreButton}
                  className={classes.loadMoreButton}
                  data-pulse-name={ANALYTICS_NAMES.LOAD_MORE}
                >
                  <Text
                    size={TypographyT5}
                    color={NeutralColors.text.default}
                    weight={FontWeights.semibold}
                  >
                    {translate(!isLoading && loadMoreButtonText)}
                  </Text>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default memo(Content);
